import React, { useState } from "react";
import clsx from "clsx/lite";
import handleFormChange from "helpers/handle-form-change";
import { validatePhoneNumber } from "helpers/validations";
import ErrorMessage from "components/error-message";
import useSignupWithTags from "hooks/useSignupWithTags";
import { trackb2bRegistration } from "analytics";
import { useCustomer } from "hooks/useStore";
import FieldRenderer from "../field-rendered";
import Loader from "components/loader";

const MultiStepForm = ({ tag = "MOD PRO" }) => {
	const [currentStep, setCurrentStep] = useState(1);
	return (
		<div className="min-h-160 md:min-h-128 w-full">
			<StepForm currentStep={currentStep} setCurrentStep={setCurrentStep} tag={tag} />
		</div>
	);
};

const StepForm = ({ currentStep, setCurrentStep, tag }) => {
	const customerData = useCustomer(["email", "firstName", "lastName", "isAuthenticated"]);
	const isAuthenticated = customerData.isAuthenticated && customerData.email;
	const formInitialState = createInitialFormState(customerData);
	const isLastStep = currentStep === steps.length;

	const track = () => {
		trackb2bRegistration({
			value: `Step ${currentStep} of ${steps.length} / form fill completion`,
		});
	};

	const { formState, setFormState, errors, setErrors, loading, formSubmit } = useSignupWithTags({
		initialState: formInitialState,
		tags: isLastStep ? ["MOD PRO SUBMIT"] : [tag],
		track,
	});

	const currentStepData = steps[currentStep - 1];

	const handleSubmit = async (e) => {
		e.preventDefault();
		const wasSubmitted = await formSubmit(e);
		if (wasSubmitted) setCurrentStep((prev) => prev + 1);
	};

	if (loading) return <LoadingIndicator />;

	return (
		<form
			onSubmit={isLastStep ? formSubmit : handleSubmit}
			onChange={(e) => handleFormChange(e, setFormState)}
			className="flex flex-col w-full min-h-160 md:min-h-128"
		>
			<StepNavigation steps={steps} currentStep={currentStep} />
			<StepContent step={currentStepData} formState={formState} errors={errors}
				setFormState={setFormState} onValidatePhone={setErrors} isAuthenticated={isAuthenticated}
			/>
			{errors.form && <ErrorMessage message={errors.form} className="justify-center pb-4" />}
			<FormNavigation currentStep={currentStep} totalSteps={steps.length}
				onBack={() => setCurrentStep((prev) => prev - 1)} loading={loading}
				canProceed={canProceedToNextStep(currentStepData, formState, isAuthenticated)}
			/>
		</form>
	);
};

const LoadingIndicator = () => (
	<div className="m-auto size-full content-center">
		<Loader />
	</div>
);

const StepNavigation = ({ steps, currentStep }) => (
	<div className="flex justify-between px-4 md:px-0">
		{steps.map((step, index) => (
			<StepNavigationItem key={step.title} step={step} index={index}
				currentStep={currentStep} totalSteps={steps.length}
			/>
		))}
	</div>
);

const StepNavigationItem = ({ step, index, currentStep, totalSteps }) => (
	<div className={clsx("flex", index === totalSteps - 1 ? "w-14 md:w-20 lg:w-14" : "w-full")}>
		<div className="flex flex-1 flex-col gap-3.5 items-center md:items-baseline w-[inherit] min-w-14">
			<span className={clsx("size-14 flex-center text-2xl font-medium", currentStep === index + 1 ? "bg-beige" : "bg-lightBeige")}>
				{index + 1}
			</span>
			<p className={clsx("w-min text-center md:text-start", currentStep === index + 1 && "text-beige")}>
				{step.title}
			</p>
		</div>
		{index < totalSteps - 1 && <div className="border-t-2 border-black w-full mt-7 mx-4" />}
	</div>
);

const StepContent = ({ step, formState, errors, setFormState, onValidatePhone, isAuthenticated }) => (
	<section className="pt-9">
		<h2 className="font-bold text-2xl pb-2">{step.title}</h2>
		<p>{step.subtitle}</p>
		<div className="py-8 flex flex-col md:grid grid-cols-2 gap-x-6 gap-y-4">
			{step.fields.map((field) => {
				if (field.name === "password" && isAuthenticated) {
					return null;
				}

				if (field.name === "phoneNumber") {
					return (
						<div key={field.name} className={!isAuthenticated ? "col-span-2" : ""}>
							<FieldRenderer
								field={{
									...field,
									required: true,
									onChange: (e) => validatePhoneNumber(e, onValidatePhone),
								}}
								formState={formState}
								errors={errors}
								setFormState={setFormState}
							/>
						</div>
					);
				}

				return (
					<div key={field.name} className={field.colSpan === 2 ? "col-span-2" : ""}>
						<FieldRenderer
							field={{
								...field,
								disabled: ["email"].includes(field.name) && isAuthenticated,
								required: field.name === "password" && !isAuthenticated,
							}}
							formState={formState}
							errors={errors}
							setFormState={setFormState}
						/>
					</div>
				);
			})}
		</div>
	</section>
);

const FormNavigation = ({ currentStep, totalSteps, onBack, canProceed, loading }) => (
	<div className="flex gap-5 mt-auto">
		{currentStep < totalSteps
			?
			<button type="submit" className="btn-secondary py-5" disabled={!canProceed || loading}>
				Next Step
			</button>
			:
			<button type="submit" className="py-5 btn-primary" disabled={loading}>
				Submit
			</button>
		}
		{currentStep > 1 &&
			<button type="button" onClick={onBack} className="text-secondary">
				Back
			</button>
		}
	</div>
);

const canProceedToNextStep = (step, formState, isAuthenticated) => {
	return step.fields.every(field => {
		if (field.required) {
			if (field.name === "password" && isAuthenticated) return true;
			return formState[field.name];
		}
		return true;
	});
};

const createInitialFormState = ({ email, firstName, lastName }) => ({
	firstName: firstName || "",
	lastName: lastName || "",
	email: email || "",
	password: "",
	phoneNumber: "",
	acceptsMarketing: true,
	company: "",
	companyAddress: "",
	jobTitle: "",
	companySize: "",
	role: "",
	additionalInfo: "",
});

const steps = [
	{
		title: "Personal Information",
		subtitle: "Fill out the form to get started today!",
		fields: [
			{ name: "firstName", title: "First Name", placeholder: "First Name", required: true },
			{ name: "lastName", title: "Last Name", placeholder: "Last Name", required: true },
			{ name: "email", title: "Email", placeholder: "Email", required: true },
			{ name: "password", title: "Password", type: "password", placeholder: "Password", required: true },
			{ name: "phoneNumber", title: "Phone Number", type: "tel", placeholder: "Phone Number", required: true },
		],
	},
	{
		title: "Project Details",
		subtitle: "Enter your project details.",
		fields: [
			{ name: "company", title: "Company Name" },
			{
				name: "companySize",
				title: "Company Size",
				type: "dropdown",
				options: ["", "1-10", "10-50", "50-100", "100+"],
				placeholder: "Company Size",
			},
			{
				name: "role",
				title: "Your Role",
				type: "dropdown",
				options: ["", "Founder / CEO", "Director", "Manager", "Other"],
				colSpan: 2,
				placeholder: "Your Role",
			},
		],
	},
	{
		title: "Additional Information",
		subtitle: "Let us know any special requirements or questions.",
		fields: [
			{
				name: "additionalInfo",
				title: "Additional Info",
				type: "textarea",
				placeholder: "If you have any special requirements or questions, please include them here (optional).",
				colSpan: 2,
			},
		],
	},
];

export default MultiStepForm;
